<template>
  <div id="mapViewer">
    <Map
      v-if="this.asMap"
      ref="mapboxMap"
      :mapSpec="mapSpec"
      :disableScrollZoom="disableScrollZoom"
      :disableLeftPanel="disableLeftPanel"
      :disableSearch="disableSearch"
      :searchAlwaysVisible="searchAlwaysVisible"
      :customSearchButtonLabel="customSearchButtonLabel"
      :homeBounds="homeBounds"
    ></Map>

    <div v-if="this.asList" class="list-container">
      <div class="row header-row icon-cell"></div>
      <div class="row header-row name-cell">
        <span class="name-cell">{{ $t('map_as_list.headers.name') }}</span>
      </div>
      <div class="row header-row elevation-cell">
        <span class="elevation-cell">{{ $t('map_as_list.headers.elevation') }}</span>
      </div>
      <div class="row header-row status-cell">
        <span class="status-cell">{{ $t('map_as_list.headers.status') }}</span>
      </div>
      <div class="row header-row detail-cell">
        <span class="detail-cell">{{ $t('map_as_list.headers.detail') }}</span>
      </div>
      <div class="row header-row last-update-cell">
        <span class="last-update-cell">{{ $t('map_as_list.headers.last_updated') }}</span>
      </div>
      <template v-for="mapElement of this.asListMapElements">
        <div :class="rowClassForMapElement(mapElement)" :key="mapElement.id + '_icon'">
          <span class="icon-cell"
            ><img v-if="iconForMapElement(mapElement)" :src="iconForMapElement(mapElement)"
          /></span>
        </div>
        <div :class="rowClassForMapElement(mapElement)" :key="mapElement.id + '_name'">
          <span class="name-cell">{{ nameForMapElement(mapElement) }}</span>
        </div>
        <div :class="rowClassForMapElement(mapElement)" :key="mapElement.id + '_elevation'">
          <span
            class="elevation-cell"
            v-if="mapElementCanHaveElevation(mapElement) && !mapElementHaveElevation(mapElement)"
            >{{ $t('map_as_list.elevation_not_available') }}</span
          >
          <span class="elevation-cell">
            <span v-if="elevationGainForMapElement(mapElement)"
              ><img src="@/assets/icons/ElevationGainIcon.png" />{{
                elevationGainForMapElement(mapElement)
              }}</span
            >
            <span v-if="elevationLossForMapElement(mapElement)"
              ><img src="@/assets/icons/ElevationLossIcon.png" />{{
                elevationLossForMapElement(mapElement)
              }}</span
            >
          </span>
        </div>
        <div
          :class="rowClassForMapElement(mapElement)"
          :style="statusStyleForMapElement(mapElement)"
          :key="mapElement.id + '_status'"
        >
          <span class="status-cell">{{ statusForMapElement(mapElement) }}</span>
          <div class="small-screen detail-cell">{{ detailForMapElement(mapElement) }}</div>
        </div>
        <div :class="rowClassForMapElement(mapElement)" :key="mapElement.id + '_detail'">
          <span class="detail-cell large-screen">{{ detailForMapElement(mapElement) }}</span>
        </div>
        <div :class="rowClassForMapElement(mapElement)" :key="mapElement.id + '_lastUpdate'">
          <div class="last-update-cell">{{ lastUpdateForMapElement(mapElement) }}</div>
        </div>
      </template>
    </div>
    <div v-if="this.asJSON">As a json TODO</div>
  </div>
</template>

<script>
import Map from '@/views/ui/map/Map.vue';
import FromOMBMapSpec from '@/map/spec/FromOMBMapSpec';
import PublisherService from '@/service/PublisherService';
import MapService from '@/service/MapService';
import MapObjectStatusService from '@/service/MapObjectStatusService';
import LangUtils from '@/utils/LangUtils';
import FormatUtils from '@/utils/FormatUtils';
import { fromArray } from '@/gis/Location';
import * as moment from 'moment';

export default {
  name: 'MapDetail',
  components: {
    Map,
  },
  props: [
    'publisherId',
    'bundleId',
    'mapId',
    'mapDefinitionFilename',
    'format',
    'displayLanguage',
    'customSearchButtonLabel',
    'topLeftBounds',
    'bottomRightBounds',
  ],
  data() {
    return {
      internalNavigation: false,
      mapSpec: null,
      disableScrollZoom: false,
      disableSearch: false,
      disableLeftPanel: false,
      searchAlwaysVisible: false,
      excludePOIs: false,
      publisherService: new PublisherService(),
      mapService: new MapService(),
      mapObjectStatusService: new MapObjectStatusService(this.$i18n.locale),
      asListMapElements: null,
      mapObjectStatusTemplates: null,
      allMapObjectStatus: [],
      homeBounds: null,
    };
  },
  computed: {
    fullscreen() {
      return !this.internalNavigation;
    },

    language() {
      return this.displayLanguage
        ? this.displayLanguage
        : this.$route.query.lang
        ? this.$route.query.lang
        : navigator.language.substring(0, 2);
    },

    asMap() {
      return this.format == 'map' || (this.format != 'list' && this.format != 'json');
    },

    asList() {
      return this.format == 'list';
    },

    asJSON() {
      return this.format == 'json';
    },
  },

  watch: {
    allMapObjectStatus: function () {
      if (this.allMapObjectStatus) {
        this.$forceUpdate();
      }
    },
  },

  methods: {
    loadMapSpec(publisherId, bundleId, mapId) {
      const mapSpec = new FromOMBMapSpec(
        null,
        publisherId,
        bundleId,
        mapId,
        this.$i18n.locale,
        this.$i18n,
        false
      );
      mapSpec
        .load()
        .then(() => {
          this.mapSpec = mapSpec;
          document.title = this.mapSpec.name != '' ? 'Ondago - ' + this.mapSpec.name : 'Ondago';

          if (this.asMap) {
            this.$refs.mapboxMap?.refresh();
          } else if (this.asList) {
            this.mapSpec.mapObjectStatusDataStream.observe().subscribe((data) => {
              if (data) {
                this.mapObjectStatusTemplates = data.mapObjectStatusTemplates;
                this.allMapObjectStatus = data.mapObjectStatusList;
              }

              const mapElements = this.mapSpec.extractInteractiveMapElements(true);
              const filteredMapElements = [];
              for (var i = 0; i < mapElements.length; i++) {
                const mapElement = mapElements[i];

                if (mapElement.type == 'layer') {
                  if (i + 1 < mapElements.length) {
                    var layerIsEmpty = true;
                    for (var j = i + 1; j < mapElements.length; j++) {
                      if (mapElements[j].type == 'layer') {
                        break;
                      } else if (this.canBeIncludedInList(mapElements[j])) {
                        layerIsEmpty = false;
                        break;
                      }
                    }

                    if (!layerIsEmpty) {
                      filteredMapElements.push(mapElement);
                    }
                  }
                } else if (this.canBeIncludedInList(mapElement)) {
                  filteredMapElements.push(mapElement);
                }
              }

              this.asListMapElements = filteredMapElements;
            });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    canBeIncludedInList(mapElement) {
      var includeInList = !this.excludePOIs || mapElement.type != 'point';
      if (includeInList) {
        const status = this.findMapObjectStatusForMapElement(mapElement.id);
        if (status) {
          const template = this.findMapObjectStatusTemplate(status.mapObjectStatusTemplateId);
          if (template) {
            includeInList = !template.excludeFromWebViewerList;
          }
        }
      }

      return includeInList;
    },

    _handleMessageEvent(event) {
      const eventData = event.data;
      if (eventData) {
        if (eventData.name == 'selectObject' || eventData.type == 'selectObject') {
          const objectId = eventData.objectId || eventData.id;
          if (objectId) {
            this.$refs.mapboxMap?.selectMapElement(objectId, true);
          }
        }
        if (eventData.name == 'centerOnBounds' || eventData.type == 'centerOnBounds') {
          const { topLeftBounds, bottomRightBounds } = eventData;
          const topLeft = fromArray(topLeftBounds);
          const bottomRight = fromArray(bottomRightBounds);
          this.homeBounds = {topLeft, bottomRight}
        }
      }
    },

    flattenMapElement(mapElement, result) {
      if (mapElement.children) {
        for (const child of mapElement.children) {
          result.push(child);
        }
      }
    },

    rowClassForMapElement(mapElement) {
      var rowClass = '';
      if (mapElement.type == 'layer') {
        rowClass = 'row layer-row';
      } else {
        rowClass = 'row normal-row';
      }

      if (mapElement.type != 'layer') {
        const index = this.asListMapElements.indexOf(mapElement);
        if (!(index % 2)) {
          rowClass = rowClass + ' odd-row';
        } else {
          rowClass = rowClass + ' even-row';
        }
      }

      return rowClass;
    },

    statusStyleForMapElement(mapElement) {
      const status = this.findMapObjectStatusForMapElement(mapElement.id);
      if (status) {
        const template = this.findMapObjectStatusTemplate(status.mapObjectStatusTemplateId);
        if (template) {
          return 'color: ' + template.color;
        } else {
          return '';
        }
      } else {
        return '';
      }
    },

    iconForMapElement(mapElement) {
      return mapElement.iconFileName;
    },

    nameForMapElement(mapElement) {
      return LangUtils.valueForLanguage(mapElement.name, this.$i18n.locale);
    },

    mapElementCanHaveElevation(mapElement) {
      if (mapElement.type == 'path' || mapElement.type == 'svgPath') {
        return true;
      }

      return false;
    },

    mapElementHaveElevation(mapElement) {
      if (mapElement.type == 'path' || mapElement.type == 'svgPath') {
        const trailInformation = mapElement.trailInformation;
        if (trailInformation) {
          return trailInformation.elevationGain || trailInformation.elevationLoss;
        }
      }

      return false;
    },

    elevationGainForMapElement(mapElement) {
      if (mapElement.type == 'path' || mapElement.type == 'svgPath') {
        const trailInformation = mapElement.trailInformation;
        if (trailInformation) {
          if (trailInformation.elevationGain) {
            return FormatUtils.formatDistance(trailInformation.elevationGain);
          }
        }
      }

      return null;
    },

    elevationLossForMapElement(mapElement) {
      if (mapElement.type == 'path' || mapElement.type == 'svgPath') {
        const trailInformation = mapElement.trailInformation;
        if (trailInformation) {
          if (trailInformation.elevationLoss) {
            return FormatUtils.formatDistance(trailInformation.elevationLoss);
          }
        }
      }

      return null;
    },

    statusForMapElement(mapElement) {
      if (mapElement.type != 'layer') {
        const status = this.findMapObjectStatusForMapElement(mapElement.id);
        if (status) {
          const template = this.findMapObjectStatusTemplate(status.mapObjectStatusTemplateId);
          if (template) {
            return LangUtils.valueForLanguage(template.title, this.$i18n.locale);
          } else {
            return this.$t('map_as_list.no_status');
          }
        } else {
          return this.$t('map_as_list.no_status');
        }
      } else {
        return '';
      }
    },

    detailForMapElement(mapElement) {
      if (mapElement.type != 'layer') {
        const status = this.findMapObjectStatusForMapElement(mapElement.id);
        if (status) {
          if (status.statusString) {
            return LangUtils.valueForLanguage(status.statusString, this.$i18n.locale);
          } else {
            return null;
          }
        } else {
          return null;
        }
      } else {
        return '';
      }
    },

    lastUpdateForMapElement(mapElement) {
      if (mapElement.type != 'layer') {
        const status = this.findMapObjectStatusForMapElement(mapElement.id);
        if (status) {
          if (status.lastUpdate) {
            return moment(new Date(status.lastUpdate)).format('YYYY-MM-DD hh:mm');
          } else {
            return this.$t('map_as_list.last_update_not_available');
          }
        } else {
          return null;
        }
      } else {
        return '';
      }
    },

    findMapObjectStatusForMapElement(mapElementId) {
      if (this.allMapObjectStatus) {
        for (var mapObjectStatus of this.allMapObjectStatus) {
          if (mapObjectStatus.mapObjectId == mapElementId) {
            return mapObjectStatus;
          }
        }
      }
      return null;
    },

    findMapObjectStatusTemplate(mapObjectStatusTemplateId) {
      if (this.mapObjectStatusTemplates) {
        for (var mapObjectStatusTemplate of this.mapObjectStatusTemplates) {
          if (mapObjectStatusTemplate.id == mapObjectStatusTemplateId) {
            return mapObjectStatusTemplate;
          }
        }
      }
      return null;
    },

    loadFromPublisherAndMap() {
      this.mapService
        .loadMapForPublisher(this.publisherId, this.mapId)
        .then((map) => {
          if (map) {
            if (map.bundleIds) {
              var bundleId = null;
              for (var id in map.bundleIds) {
                if (map.bundleIds[id] == true) {
                  bundleId = id;
                  break;
                }
              }

              if (bundleId) {
                this.loadMapSpec(this.publisherId, bundleId, this.mapId);
              } else {
                console.log('bundle not found');
              }
            } else {
              console.log('no bundle');
            }
          } else {
            console.log('map not found');
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    loadFromBundle() {
      this.publisherService
        .loadPublishersForBundle(this.bundleId)
        .then((publishers) => {
          var publisherId = null;
          if (publishers.length > 0) {
            publisherId = publishers[0].id;
          }

          if (publisherId) {
            this.mapService
              .loadMapsByMapDefinitionFilename(publisherId, this.mapDefinitionFilename + '.json')
              .then((maps) => {
                if (maps.length > 0) {
                  const map = maps[0];
                  this.loadMapSpec(publisherId, this.bundleId, map.id);
                }
              })
              .catch((error) => {
                console.log(error);
              });
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },

  mounted() {
    const external = 'true' == this.$route.query.external;

    this.$i18n.locale = this.language;
    this.disableScrollZoom =
      this.$route.query.disableScrollZoom == 'true' || this.$route.query.mousewheel == 'false';
    this.disableSearch = external;
    this.disableLeftPanel = external;
    this.searchAlwaysVisible = this.$route.query.searchAlwaysVisible == 'true';
    this.excludePOIs = this.$route.query.excludePOIs == 'true';

    if (this.publisherId && this.mapId) {
      this.loadFromPublisherAndMap();
    } else {
      this.loadFromBundle();
    }

    window.addEventListener(
      'message',
      (event) => {
        this._handleMessageEvent(event);
      },
      false
    );
    this.homeBounds =
      this.topLeftBounds && this.bottomRightBounds
        ? { topLeft: this.topLeftBounds, bottomRight: this.bottomRightBounds }
        : null;
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.internalNavigation = from.name != null;
      vm.$store.state.fullscreen = !vm.internalNavigation;
    });
  },

  beforeRouteLeave(to, from, next) {
    this.$store.state.fullscreen = false;
    next();
  },
};
</script>

<style>
#mapViewer {
  position: absolute;
  width: 100%;
  height: 100%;
}

.ondago-logo {
  display: block !important;
  padding-right: 16px;
  text-align: right;
}

.ondago-logo span {
  color: #999999;
  font-size: 10px;
  margin-right: 8px;
}

.ondago-logo img {
  height: 20px;
}

.small-screen {
  display: none;
}

.list-container {
  display: grid;
  grid-template-columns: 40px minmax(200px, auto) minmax(120px, 200px) minmax(100px, auto) auto auto;
  list-style-type: none;
  margin: 16px;
  margin-top: 0px;
  text-align: left;
}

.row {
  padding-top: 8px;
  padding-bottom: 8px;
  display: table-row;
}

.header-row {
  background-color: #ffffff;
}

.header-row .name-cell {
  font-weight: bold;
}

.header-row .elevation-cell {
  font-weight: bold;
}

.header-row .status-cell {
  font-weight: bold;
}

.header-row .detail-cell {
  font-weight: bold;
}

.header-row .last-update-cell {
  font-weight: bold;
}

.layer-row {
  background-color: #484848;
}

.layer-row .name-cell {
  color: #ffffff;
  font-weight: bold;
  grid-column: 1 / 2;
}

.normal-row .icon-cell {
  padding: 0px;
}

.normal-row .icon-cell img {
  width: 40px;
  height: 40px;
}

.normal-row .name-cell {
  align-self: center;
  color: #000000;
  font-weight: bold;
}

.normal-row .elevation-cell {
  align-self: center;
  color: #000000;
}

.normal-row .elevation-cell span {
  margin-right: 16px;
}

.normal-row .elevation-cell img {
  width: 30px;
}

.normal-row .status-cell {
  align-self: center;
  font-weight: bold;
  margin: 0px !important;
  padding: 0px !important;
}

.detail-cell {
  align-self: center;
}

.last-update-cell {
  align-self: center;
}

.odd-row {
  background-color: #f5f5f5;
}

.even-row {
  background-color: #ffffff;
}

@media only screen and (max-width: 1000px) {
  .list-container {
    display: grid;
    grid-template-columns: 25px minmax(50px, 220px) 0px minmax(50px, 150px) 0px 0px;
    list-style-type: none;
    text-align: left;
    padding: 0px;
  }

  .row {
    margin: 0px;
  }

  .small-screen {
    display: block;
  }

  .list-container {
    font-size: 10pt;
  }

  .normal-row .icon-cell img {
    width: 25px;
    height: 25px;
  }

  .layer-row span {
    padding: 0px;
    grid-column: span;
  }

  .normal-row span {
    padding: 0px;
  }

  .normal-row .elevation-cell {
    display: none;
  }

  .elevation-cell span {
    display: block;
  }

  .normal-row .last-update-cell {
    display: none;
  }

  .normal-row .detail-cell {
    color: #000000;
    margin: 0px;
    padding: 0px;
  }

  .header-row .detail-cell {
    display: none;
  }

  .header-row .last-update-cell {
    display: none;
  }

  .large-screen {
    display: none;
  }
}
</style>
